/**
 * @module plugins/musicfavourite/adapter/favouriteItemsCache
 *
 * Client-side cache of favourite items, retrieved from the specified URL. The cache get() method returns a Deferred
 * object, allowing multiple simultaneous calls to be supported by a single AJAX request. The cache can optionally be
 * set to expire after a specified lifetime, with subsequent calls triggering a new AJAX request to refresh the data.
 *
 * The cache also supports add() and remove() calls to update its stored collection of favourite items, thus allowing
 * it to be kept in sync with any add/remove requests.
 */
define(
    'plugins/musicfavourite/adapter/favouriteItemsCache',['jquery-1.9', '../model/map', './idAvailability', 'exports'],
    function ($, Map, idAvailability, Cache) {

        'use strict';

        var HARD_LIMITS = {};

        Cache = function (url, lifetime) {
            var self = this;

            self._requestUrl = url;
            self._lifetime = lifetime;
            self._cachedData = undefined;
            self._updatePromise = undefined;
        };

        Cache.prototype.get = function () {
            var self = this;

            if (self._cachedData) {
                return $.Deferred().resolve(self._cachedData);
            } else if (self._updatePromise) {
                return self._updatePromise;
            } else {
                return self.update();
            }
        };

        Cache.prototype.update = function () {
            var self = this;

            var updatePromise = idAvailability.ajaxIfIdAvailable({
                method: 'GET',
                url: self._requestUrl,
                headers: { 'Cache-control': 'max-age=10' },
                contentType: 'application/json',
                dataType: 'jsonp'
            }).then(function (data) {
                if (self._lifetime) {
                    setTimeout(function () {
                        self._updatePromise = undefined;
                        self._cachedData = undefined;
                    }, self._lifetime);
                }

                self._cachedData = data && new Map(data);
                return self._cachedData;
            }).fail(function (error) {
                // NOTE: failure handlers are not invoked for failed JSONP requests. This failure handler exists to
                // catch the case when we don't even *attempt* the request, and instead directly return a rejected
                // Deferred with an explicit error.
                self._updatePromise = undefined;
                self._cachedData = undefined;
                return error;
            });

            self._updatePromise = updatePromise;
            return updatePromise;
        };

        Cache.prototype.canAdd = function (item) {
            if (this._cachedData) {
                var type = item.type;
                var hardLimit = HARD_LIMITS[type];

                // Check limits by type
                if (hardLimit) {
                    var favouriteItemsOfType = this._cachedData.filter(function(x) { return x.type == type; });
                    if (favouriteItemsOfType.length && favouriteItemsOfType.length >= hardLimit) {
                        return false;
                    }
                }
            }
            return true;
        };

        Cache.prototype.add = function (item) {
            if (this._cachedData) {
                this._cachedData.addAll(item);
            }
        };

        Cache.prototype.remove = function (item) {
            if (this._cachedData) {
                this._cachedData.remove(item);
            }
        };

        return Cache;
    }
);

